// ============================================================
// A BLAST'S TEMPLATE ON THE MAP (plan design-gaps, T2; the areas page). Every
// victim of one template carries it on its beat (engine/display/beats
// `AreaPlay`); this draws it ONCE, from whichever of their beats is playing:
// a scattered throw's template sliding from where it was aimed to where it
// landed, a dashed arrow behind it; then, as the round lands, the detonation -
// a disc on the ground fading as a ring runs out past the template's edge. The
// primary goes off first; each spawned copy, violet, goes off after it from its
// spawner (its victims' beats hold until then).
// ============================================================

import type { Camera } from '../../engine/battlefield/projection';
import { SCENE_VIEW, project } from '../../engine/battlefield/projection';
import { ageOf, SLIDE_MS, type AreaPlay, type DrawnBeat } from '../../engine/display/beats';
import { lerp, progress } from '../../engine/display/timeline';
import type { Vec3 } from '../../engine/battlefield/types';
import { groundRing } from './BeatMarks';
import styles from '../../styles/Display.module.css';

/** The detonation's life, ms from the landing: the disc fading, the ring running out to the edge and past it. */
const GOES_OFF_MS = 740;
const RING_PAST_M = 1.5;
/** A still screen shows the detonation this far in. */
const STILL_AT = 0.5;

/** Each template once: the first of its victims' beats playing, keyed by where it is, its size and its step. */
function templatesOf(drawn: readonly DrawnBeat[]): { key: string; area: AreaPlay; t: number | null }[] {
  const seen = new Map<string, { key: string; area: AreaPlay; t: number | null }>();
  for (const { beat, t } of drawn) {
    const a = beat.area;
    const key = a && `${a.centre.x},${a.centre.z},${a.r},${a.copy}`;
    if (key && !seen.has(key)) seen.set(key, { key, area: a, t });
  }
  return [...seen.values()];
}

/** Every template the playing beats were caught in: its slide, then its detonation. */
export default function AreaBursts({ drawn, camera }: { drawn: readonly DrawnBeat[]; camera: Camera }) {
  const templates = templatesOf(drawn);
  if (!templates.length) return null;
  return (
    <g data-testid="area-bursts" pointerEvents="none">
      {templates.map(({ key, area, t }) => (
        <g key={key} data-copy={area.copy || undefined}>
          {area.from && <Slide area={area} from={area.from} t={t} camera={camera} />}
          <GoesOff area={area} t={t} camera={camera} />
        </g>
      ))}
    </g>
  );
}

/** A scattered template sliding into place, the arrow from its aim behind it (until it goes off). */
function Slide({ area, from, t, camera }: { area: AreaPlay; from: Vec3; t: number | null; camera: Camera }) {
  if (t === null || t >= area.goesOffAt) return null;
  const k = progress(t, 0, SLIDE_MS);
  const at = { x: lerp(from.x, area.centre.x, k), y: area.centre.y, z: lerp(from.z, area.centre.z, k) };
  const [a, b] = [project(from, camera, SCENE_VIEW), project(at, camera, SCENE_VIEW)];
  return (
    <g data-testid="area-slide">
      <line className={styles.areaSlideArrow} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
      <polygon className={styles.areaTemplate} points={groundRing(at, area.r, camera)} />
    </g>
  );
}

/** The detonation: a disc on the ground fading, a ring running out past the edge. */
function GoesOff({ area, t, camera }: { area: AreaPlay; t: number | null; camera: Camera }) {
  const k = ageOf(area.goesOffAt, GOES_OFF_MS, t, STILL_AT);
  if (k === null) return null;
  const ringM = lerp(0.3, area.r + RING_PAST_M, k);
  return (
    <g data-testid="area-detonation" className={area.copy ? styles.areaCopy : styles.areaPrimary}>
      <polygon className={styles.areaDisc} points={groundRing(area.centre, area.r, camera)} opacity={0.45 * (1 - k)} />
      <polygon className={styles.areaRing} points={groundRing(area.centre, ringM, camera)} opacity={1 - k} />
    </g>
  );
}
